import {
  Component,
  OnInit,
  Inject,
  PLATFORM_ID,
  ViewEncapsulation,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import Swal from 'sweetalert2';
import { finalize } from 'rxjs';

import { ResigteringService } from '../auth/resigtering.service';
import { LoginService } from '../auth/login.service';
import { DeviceDetectorService } from '../../../services/device-detector.service';

interface AuthResponse {
  token?: string;
  user?: {
    id?: number;
    username?: string;
    name?: string;
    first_name?: string;
    last_name?: string;
    phone?: string;
    email?: string;

    type?: number | string;
    user_type?: 'candidate' | 'admin' | 'company';

    company_id?: number | null;
    doctor_id?: number | null;

    user_cv?: string | null;
    user_cv_url?: string | null;

    access_pages?: string[];
    has_access_to_phone_numbers?: boolean;
  };

  data?: {
    token?: string;
    user?: {
      id?: number;
      username?: string;
      name?: string;
      first_name?: string;
      last_name?: string;
      phone?: string;
      email?: string;

      type?: number | string;
      user_type?: 'candidate' | 'admin' | 'company';

      company_id?: number | null;
      doctor_id?: number | null;

      user_cv?: string | null;
      user_cv_url?: string | null;

      access_pages?: string[];
      has_access_to_phone_numbers?: boolean;
    };
  };
}

@Component({
  selector: 'app-resigtering',
  templateUrl: './resigtering.component.html',
  styleUrl: './resigtering.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class ResigteringComponent implements OnInit {
  formInfo!: FormGroup;
  contentReady = false;

  isPasswordVisible = false;
  isUpdatePage = false;
  editedUserId: any;

  isMobile = false;
  isAr = false;
  private isBrowser: boolean;

  selectedCvFile: File | null = null;
  cvDragOver = false;
  cvErrorKey = '';

  private readonly fixedType = '9';
  private readonly fixedUserType = 'candidate';

  private readonly maxCvFileSize = 5 * 1024 * 1024;

  constructor(
    private fb: FormBuilder,
    private registerService: ResigteringService,
    private loginService: LoginService,
    private router: Router,
    private route: ActivatedRoute,
    private deviceService: DeviceDetectorService,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit(): void {
    this.initForm();

    if (!this.isBrowser) return;

    this.isMobile = this.deviceService.isMobileDevice();
    this.isAr = localStorage.getItem('language') === 'ar';

    this.route.queryParams.subscribe((params) => {
      if (params['id']) {
        this.isUpdatePage = true;
        this.editedUserId = params['id'];
        this.makePasswordOptionalForUpdate();
      }
    });
  }

  private initForm(): void {
    this.formInfo = this.fb.group(
      {
        username: ['', [Validators.required, Validators.minLength(3)]],
        first_name: ['', [Validators.required, Validators.minLength(2)]],
        last_name: ['', [Validators.required, Validators.minLength(2)]],
        phone: ['', [Validators.maxLength(15)]],
        password: ['', [Validators.required, Validators.minLength(8)]],
        c_Password: ['', [Validators.required]],
      },
      { validators: this.passwordsMatch }
    );
  }

  private makePasswordOptionalForUpdate(): void {
    const password = this.formInfo.get('password');
    const confirmPassword = this.formInfo.get('c_Password');

    password?.setValidators([Validators.minLength(8)]);
    confirmPassword?.clearValidators();

    password?.updateValueAndValidity();
    confirmPassword?.updateValueAndValidity();
  }

  private passwordsMatch(group: AbstractControl): ValidationErrors | null {
    const password = group.get('password')?.value;
    const confirmPassword = group.get('c_Password')?.value;

    if (!password && !confirmPassword) return null;

    return password === confirmPassword ? null : { passwordMismatch: true };
  }

  isInvalid(controlName: string): boolean {
    const control = this.formInfo.get(controlName);
    return !!(control && control.touched && control.invalid);
  }

  isConfirmPasswordInvalid(): boolean {
    const confirm = this.formInfo.get('c_Password');

    return !!(
      confirm &&
      confirm.touched &&
      (confirm.invalid || this.formInfo.hasError('passwordMismatch'))
    );
  }

  togglePasswordVisibility(): void {
    this.isPasswordVisible = !this.isPasswordVisible;
  }

  goCompanyAccount(): void {
    this.router.navigate(['/iamhiring']);
  }

  onCvFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (file) {
      this.setCvFile(file);
    }

    input.value = '';
  }

  onCvDragOver(event: DragEvent): void {
    event.preventDefault();

    if (this.contentReady) return;

    this.cvDragOver = true;
  }

  onCvDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.cvDragOver = false;
  }

  onCvDrop(event: DragEvent): void {
    event.preventDefault();

    this.cvDragOver = false;

    if (this.contentReady) return;

    const file = event.dataTransfer?.files?.[0];

    if (file) {
      this.setCvFile(file);
    }
  }

  setCvFile(file: File): void {
    this.cvErrorKey = '';

    const fileName = file.name.toLowerCase();

    const isAllowed =
      file.type === 'application/pdf' ||
      file.type === 'application/msword' ||
      file.type ===
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      fileName.endsWith('.pdf') ||
      fileName.endsWith('.doc') ||
      fileName.endsWith('.docx');

    if (!isAllowed) {
      this.selectedCvFile = null;
      this.cvErrorKey = 'auth.register.cvInvalidType';
      return;
    }

    if (file.size > this.maxCvFileSize) {
      this.selectedCvFile = null;
      this.cvErrorKey = 'auth.register.cvMaxSize';
      return;
    }

    this.selectedCvFile = file;
  }

  removeCvFile(event: MouseEvent): void {
    event.stopPropagation();

    this.selectedCvFile = null;
    this.cvDragOver = false;
    this.cvErrorKey = '';
  }

  formatFileSize(bytes: number): string {
    if (!bytes) {
      return '0 KB';
    }

    const kb = bytes / 1024;

    if (kb < 1024) {
      return `${kb.toFixed(1)} KB`;
    }

    return `${(kb / 1024).toFixed(1)} MB`;
  }

  getFileTypeLabel(file: File | null): string {
    if (!file) {
      return 'CV';
    }

    const name = file.name.toLowerCase();

    if (name.endsWith('.pdf')) {
      return 'PDF';
    }

    if (name.endsWith('.doc')) {
      return 'DOC';
    }

    if (name.endsWith('.docx')) {
      return 'DOCX';
    }

    return 'CV';
  }

  onSubmit(): void {
    if (this.formInfo.invalid) {
      this.formInfo.markAllAsTouched();
      return;
    }

    this.contentReady = true;

    const formData = this.buildFormData();

    const request$ = this.isUpdatePage
      ? this.registerService.editUser(this.editedUserId, formData)
      : this.registerService.user_Resigter(formData);

    request$.subscribe({
      next: (res: AuthResponse) => {
        if (this.isUpdatePage) {
          this.contentReady = false;

          Swal.fire({
            icon: 'success',
            title: this.isAr
              ? 'تم تعديل الحساب بنجاح'
              : 'Account updated successfully',
            timer: 1500,
            showConfirmButton: false,
          });

          this.router.navigate(['/Home']);
          return;
        }

        const authResponse = this.normalizeAuthResponse(res);

        if (authResponse?.token) {
          this.contentReady = false;
          this.saveAuthData(authResponse);

          Swal.fire({
            icon: 'success',
            title: this.isAr
              ? 'أهلاً بيك في أحمد الشحات'
              : 'Welcome to Ahmed El-Shahat',
            timer: 1500,
            showConfirmButton: false,
          });

          this.router.navigate(['/Home']);
          return;
        }

        this.loginAfterRegister();
      },

      error: (err) => {
        this.contentReady = false;

        const message =
          err?.error?.message ||
          (this.isAr ? 'حصل خطأ، حاول مرة تانية' : 'Something went wrong');

        Swal.fire({
          icon: 'error',
          title: this.isAr ? 'خطأ' : 'Error',
          text: message,
        });
      },
    });
  }

  private loginAfterRegister(): void {
    const fv = this.formInfo.value;

    const loginPayload = {
      username: String(fv.username || '').trim(),
      password: fv.password,
    };

    this.loginService
      .user_login(loginPayload)
      .pipe(finalize(() => (this.contentReady = false)))
      .subscribe({
        next: (res: AuthResponse) => {
          const authResponse = this.normalizeAuthResponse(res);

          if (!authResponse?.token) {
            Swal.fire({
              icon: 'success',
              title: this.isAr
                ? 'تم إنشاء الحساب بنجاح'
                : 'Account created successfully',
              text: this.isAr
                ? 'من فضلك سجل دخولك'
                : 'Please login to continue',
              confirmButtonText: this.isAr ? 'تمام' : 'OK',
            });

            this.router.navigate(['/login']);
            return;
          }

          this.saveAuthData(authResponse);

          Swal.fire({
            icon: 'success',
            title: this.isAr
              ? 'أهلاً بيك في أحمد الشحات'
              : 'Welcome to Ahmed El-Shahat',
            timer: 1500,
            showConfirmButton: false,
          });

          this.router.navigate(['/Home']);
        },

        error: () => {
          Swal.fire({
            icon: 'success',
            title: this.isAr
              ? 'تم إنشاء الحساب بنجاح'
              : 'Account created successfully',
            text: this.isAr
              ? 'من فضلك سجل دخولك'
              : 'Please login to continue',
            confirmButtonText: this.isAr ? 'تمام' : 'OK',
          });

          this.router.navigate(['/login']);
        },
      });
  }

  private normalizeAuthResponse(res: AuthResponse): AuthResponse {
    return {
      token: res?.token || res?.data?.token,
      user: res?.user || res?.data?.user,
    };
  }

  private saveAuthData(res: AuthResponse): void {
    if (!this.isBrowser || !res?.token) return;

    const user = res.user;

    if (user?.username) {
      this.setLocalStorageItem('username', user.username);
    }

    if (user?.id !== undefined && user?.id !== null) {
      this.setLocalStorageItem('user_id', String(user.id));
    }

    if (user?.first_name) {
      this.setLocalStorageItem('first_name', user.first_name);
    }

    if (user?.last_name) {
      this.setLocalStorageItem('last_name', user.last_name);
    }

    if (user?.phone) {
      this.setLocalStorageItem('phone', user.phone);
    }

    if (user?.email) {
      this.setLocalStorageItem('email', user.email);
    }

    if (user?.user_type) {
      this.setLocalStorageItem('user_type', user.user_type);
    } else {
      this.setLocalStorageItem('user_type', this.fixedUserType);
    }

    if (user?.type !== undefined && user?.type !== null) {
      this.setLocalStorageItem('type', String(user.type));
    } else {
      this.setLocalStorageItem('type', this.fixedType);
    }

    if (user?.company_id !== undefined && user?.company_id !== null) {
      this.setLocalStorageItem('company_id', String(user.company_id));
    } else {
      window.localStorage.removeItem('company_id');
    }

    if (user?.doctor_id !== undefined && user?.doctor_id !== null) {
      this.setLocalStorageItem('doctor_id', String(user.doctor_id));
    } else {
      window.localStorage.removeItem('doctor_id');
    }

    if (user?.access_pages) {
      this.setLocalStorageItem(
        'access_pages',
        JSON.stringify(user.access_pages)
      );
    } else {
      window.localStorage.removeItem('access_pages');
    }

    this.setLocalStorageItem(
      'has_access_to_phone_numbers',
      String(!!user?.has_access_to_phone_numbers)
    );

    this.saveUserData(user);
    this.loginService.setToken(res.token);
  }

  private saveUserData(user: AuthResponse['user']): void {
    if (!this.isBrowser || !user) {
      return;
    }

    const cv = user.user_cv || user.user_cv_url || '';

    this.setLocalStorageItem('user', JSON.stringify(user));

    if (cv) {
      this.setLocalStorageItem('user_cv', cv);
      this.setLocalStorageItem('user_cv_url', cv);
    } else {
      window.localStorage.removeItem('user_cv');
      window.localStorage.removeItem('user_cv_url');
    }
  }

  private setLocalStorageItem(key: string, value: string): void {
    if (!this.isBrowser) return;
    window.localStorage.setItem(key, value);
  }

  private buildFormData(): FormData {
    const fv = this.formInfo.value;
    const formData = new FormData();

    formData.append('username', String(fv.username || '').trim());
    formData.append('type', this.fixedType);
    formData.append('user_type', this.fixedUserType);

    formData.append('first_name', String(fv.first_name || '').trim());
    formData.append('last_name', String(fv.last_name || '').trim());
    formData.append('phone', String(fv.phone || '').trim());

    if (this.selectedCvFile) {
      formData.append('user_cv', this.selectedCvFile);
    }

    if (!this.isUpdatePage || fv.password) {
      formData.append('password', fv.password);
      formData.append('password_confirmation', fv.c_Password);
    }

    formData.append('company_id', '');
    formData.append('doctor_id', '');

    formData.append('instagram_link', '');
    formData.append('facebook_link', '');
    formData.append('tiktok_link', '');

    formData.append('has_access_to_phone_numbers', '');
    formData.append('access_pages', '');

    return formData;
  }
}
